import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import { Icon, LatLngExpression } from 'leaflet';
import { Truck, Package, Database, Navigation, Zap, Save, Plus, ArrowRight, CreditCard as Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import useAppStore from '../store';
import { supabase } from '../lib/supabase';
import SearchableSelect from './SearchableSelect';
import DumpsterTaskQuickCreateForm from './DumpsterTaskQuickCreateForm';
import { useNavigate } from 'react-router-dom';

// Default position for the map (centered on France)
const defaultPosition: LatLngExpression = [46.603354, 1.888334];

const activeDriverIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/vehicles/camion25.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const inactiveDriverIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/vehicles/camion26.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const primaryLocationIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/icons/faviconblue.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const secondaryLocationIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/icons/favicon.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const depotIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/icons/silo.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const dumpsterIcon = new Icon({
  iconUrl: 'https://crsvlsbrxgpfesnnrfil.supabase.co/storage/v1/object/public/assets/icons/benne.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

interface MapComponentProps {
  center?: { lat: number; lng: number } | null;
  zoom?: number;
  onClick?: (e: { latlng: { lat: number; lng: number } }) => void;
  onMarkerClick?: (type: 'driver' | 'location' | 'depot' | 'dumpster', id: string) => void;
  onZoomChange?: (zoom: number) => void;
  showDrivers?: boolean;
  showDepots?: boolean;
  clientLocations?: Array<{
    id: string;
    name: string;
    position_lat: number;
    position_lng: number;
    is_primary: boolean;
    client_id: string;
  }>;
  isPickingLocation?: boolean;
  dumpsters?: Array<{
    id: string;
    name: string;
    number: string;
    position?: { lat: number; lng: number };
    clientId?: string;
    depotId?: string;
    dropDate?: string;
    driverId?: string;
    selected?: boolean;
  }>;
  showSaveGpsButton?: boolean;
  navigate?: (path: string) => void;
}

const MapController = ({ 
  center, 
  zoom,
  onZoomChange,
  onClick,
  isPickingLocation
}: { 
  center: LatLngExpression | null;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  onClick?: (e: { latlng: { lat: number; lng: number } }) => void;
  isPickingLocation?: boolean;
}) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center as LatLngExpression, zoom ?? map.getZoom());
    }
  }, [center, zoom, map]);

  useEffect(() => {
    if (onZoomChange) {
      map.on('zoomend', () => {
        onZoomChange(map.getZoom());
      });
    }
  }, [map, onZoomChange]);

  useEffect(() => {
    if (isPickingLocation && onClick) {
      const handleClick = (e: any) => {
        onClick({ latlng: e.latlng });
      };

      map.on('click', handleClick);

      return () => {
        map.off('click', handleClick);
      };
    }
    return undefined;
  }, [map, onClick, isPickingLocation]);

  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
  center = null,
  zoom = 13,
  onClick,
  onMarkerClick,
  onZoomChange,
  showDrivers = true,
  showDepots = true,
  clientLocations = [],
  isPickingLocation = false,
  dumpsters = [],
  showSaveGpsButton = true,
  navigate
}) => {
  const { drivers, depots, tasks, clients, currentUser, loadInitialData, dumpsters: allDumpsters } = useAppStore();
  const [showSaveGpsForm, setShowSaveGpsForm] = useState(false);
  const [showCreateTaskForm, setShowCreateTaskForm] = useState(false);
  const [showEditLocationForm, setShowEditLocationForm] = useState(false);
  const [selectedDumpster, setSelectedDumpster] = useState<{
    id: string;
    position: LatLngExpression;
  } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    id: string;
    name: string;
    position_lat: number;
    position_lng: number;
    is_primary: boolean;
  } | null>(null);
  const [saveGpsFormData, setSaveGpsFormData] = useState({
    name: '',
    clientId: '',
    isPrimary: false
  });
  const [editLocationFormData, setEditLocationFormData] = useState({
    name: '',
    position_lat: '',
    position_lng: ''
  });
  const [saveGpsError, setSaveGpsError] = useState<string | null>(null);
  const [editLocationError, setEditLocationError] = useState<string | null>(null);
  const [saveGpsSuccess, setSaveGpsSuccess] = useState(false);

  console.log('MapComponent - showSaveGpsForm:', showSaveGpsForm);

  const handleSaveGpsSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!selectedDumpster || !selectedDumpster.position) {
      setSaveGpsError('Position invalide');
      return;
    }

    if (!saveGpsFormData.name || !saveGpsFormData.clientId) {
      setSaveGpsError('Veuillez remplir tous les champs');
      return;
    }

    try {
      setSaveGpsError(null);
      const [lat, lng] = selectedDumpster.position as number[];
      
      const { error } = await supabase
        .from('client_locations')
        .insert({
          client_id: saveGpsFormData.clientId,
          name: saveGpsFormData.name,
          position_lat: lat,
          position_lng: lng,
          is_primary: saveGpsFormData.isPrimary,
          created_by: currentUser?.id
        });

      if (error) throw error;

      setSaveGpsSuccess(true);
      setShowSaveGpsForm(false);
      setSaveGpsFormData({
        name: '',
        clientId: '',
        isPrimary: false
      });
      setSelectedDumpster(null);

      await loadInitialData();

      setTimeout(() => {
        setSaveGpsSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error saving GPS point:', error);
      setSaveGpsError('Une erreur est survenue lors de l\'enregistrement du point GPS');
    }
  };

  const handleEditLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedLocation) {
      setEditLocationError('Location non sélectionnée');
      return;
    }

    if (!editLocationFormData.name) {
      setEditLocationError('Le nom est requis');
      return;
    }

    const lat = parseFloat(editLocationFormData.position_lat);
    const lng = parseFloat(editLocationFormData.position_lng);

    if (isNaN(lat) || isNaN(lng)) {
      setEditLocationError('Coordonnées GPS invalides');
      return;
    }

    try {
      setEditLocationError(null);

      const { error } = await supabase
        .from('client_locations')
        .update({
          name: editLocationFormData.name,
          position_lat: lat,
          position_lng: lng
        })
        .eq('id', selectedLocation.id);

      if (error) throw error;

      // Fermer le formulaire d'édition
      setShowEditLocationForm(false);
      
      // Réinitialiser les données du formulaire
      setEditLocationFormData({
        name: '',
        position_lat: '',
        position_lng: ''
      });
      
      // Désélectionner le point GPS
      setSelectedLocation(null);

      // Recharger les données
      await loadInitialData();
      
      // Rediriger vers la page d'accueil si la fonction navigate est disponible
      if (navigate) {
        navigate('/');
      }

    } catch (error) {
      console.error('Error updating location:', error);
      setEditLocationError('Une erreur est survenue lors de la modification du point GPS');
    }
  };

  const clientOptions = useMemo(() => {
    return clients.map(client => ({
      value: client.id,
      label: `${client.companyName} (${client.firstName} ${client.lastName})`
    }));
  }, [clients]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .leaflet-popup {
        z-index: 900 !important;
      }
      .leaflet-popup-content-wrapper {
        max-width: none !important;
        width: auto !important;
        min-width: 300px !important;
      }
      .leaflet-popup-content {
        margin: 13px 19px;
        width: auto !important;
      }
      .leaflet-container {
        z-index: 1;
      }
      .leaflet-popup-content .searchable-select {
        z-index: 950 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const driverMarkers = useMemo(() => {
    if (!showDrivers) return [];
    return drivers
      .filter(driver => driver.isOnline === true)
      .filter(driver => driver.position && typeof driver.position.lat === 'number' && typeof driver.position.lng === 'number')
      .map(driver => {
        const hasActiveTasks = tasks.some(task =>
          task.driverId === driver.id && task.status === 'in-progress'
        );

        return {
          id: driver.id,
          position: [driver.position!.lat, driver.position!.lng] as LatLngExpression,
          name: `${driver.firstName} ${driver.lastName}`,
          isActive: driver.isActive,
          hasActiveTasks,
          power: driver.power
        };
      });
  }, [drivers, showDrivers, tasks]);

  const depotMarkers = useMemo(() => {
    if (!showDepots) return [];
    return depots
      .filter(depot => depot.position && typeof depot.position.lat === 'number' && typeof depot.position.lng === 'number')
      .map(depot => ({
        id: depot.id,
        position: [depot.position.lat, depot.position.lng] as LatLngExpression,
        name: depot.name
      }));
  }, [depots, showDepots]);

  const dumpsterMarkers = useMemo(() => {
    // Group dumpsters by their exact GPS position
    type DumpsterWithPosition = {
      id: string;
      name: string;
      number: string;
      comment?: string;
      position: LatLngExpression;
      clientId?: string;
      depotId?: string;
      dropDate?: string;
      driverId?: string;
      placedByClientName?: string;
      isActive?: boolean;
      driver?: typeof drivers[0] | null;
    };
    const dumpstersByPosition = new Map<string, DumpsterWithPosition[]>();

    dumpsters
      .filter(dumpster => !dumpster.depotId)
      .forEach(dumpster => {
        if (!dumpster.position) return;
        const position: [number, number] = [dumpster.position.lat, dumpster.position.lng];
        const positionKey = `${position[0]},${position[1]}`;
        if (!dumpstersByPosition.has(positionKey)) {
          dumpstersByPosition.set(positionKey, []);
        }
        dumpstersByPosition.get(positionKey)!.push({
          ...dumpster,
          position: position as LatLngExpression,
          driver: dumpster.driverId ? drivers.find(d => d.id === dumpster.driverId) : null
        });
      });

    // Convert to array of grouped markers
    return Array.from(dumpstersByPosition.entries()).map(([positionKey, dumpstersAtPosition]) => ({
      positionKey,
      position: dumpstersAtPosition[0].position,
      dumpsters: dumpstersAtPosition
    }));
  }, [dumpsters, drivers]);

  return (
    <MapContainer
      center={center || defaultPosition}
      zoom={zoom}
      className="h-full w-full"
    >
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="OpenStreetMap">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            attribution='&copy; <a href="https://www.arcgis.com/">ArcGIS</a>'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      
      <MapController 
        center={center} 
        zoom={zoom} 
        onZoomChange={onZoomChange}
        onClick={onClick}
        isPickingLocation={isPickingLocation}
      />

      {driverMarkers.map(({ id, position, name, isActive, hasActiveTasks, power }) => (
        <Marker
          key={`driver-${id}`}
          position={position}
          icon={hasActiveTasks ? activeDriverIcon : inactiveDriverIcon}
          zIndexOffset={1000}
          eventHandlers={{
            click: () => onMarkerClick?.('driver', id)
          }}
        >
          <Popup>
            <div className="flex items-center gap-1">
              <span className="font-medium">{name}</span>
              {power && (
                <span className="inline-flex items-center justify-center bg-yellow-400 rounded-full w-5 h-5">
                  <Zap className="h-3 w-3 text-white" />
                </span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {isActive ? 'En ligne' : 'Hors ligne'}
            </div>
          </Popup>
        </Marker>
      ))}

      {clientLocations.map((location) => (
        <Marker
          key={`location-${location.id}`}
          position={[location.position_lat, location.position_lng] as LatLngExpression}
          icon={location.is_primary ? primaryLocationIcon : secondaryLocationIcon}
          eventHandlers={{
            click: () => onMarkerClick?.('location', location.id)
          }}
        >
          <Popup>
            <div className="font-medium">{location.name}</div>
            {location.is_primary && (
              <div className="text-sm text-blue-600">Point principal</div>
            )}
            <button
              onClick={() => {
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${location.position_lat},${location.position_lng}`,
                  '_blank'
                );
              }}
              className="mt-2 text-blue-600 hover:text-blue-800 flex items-center text-sm"
            >
              <Navigation className="h-4 w-4 mr-1" />
              Itinéraire
            </button>

            {!showSaveGpsForm && !showCreateTaskForm && !showEditLocationForm && (
              <div className="mt-3 space-y-2">
                {showSaveGpsButton && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSaveGpsForm(true);
                    }}
                    className="w-full px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer le point GPS
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDumpster({
                      id: location.id,
                      position: [location.position_lat, location.position_lng]
                    });
                    setShowCreateTaskForm(true);
                  }}
                  className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une tâche benne
                </button>

                {(currentUser?.role === 'admin' || currentUser?.role === 'logistics') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLocation(location);
                      setEditLocationFormData({
                        name: location.name,
                        position_lat: location.position_lat.toString(),
                        position_lng: location.position_lng.toString()
                      });
                      setShowEditLocationForm(true);
                    }}
                    className="w-full px-3 py-2 bg-yellow-400 text-white rounded-lg hover:bg-yellow-500 flex items-center justify-center"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Modifier le point GPS
                  </button>
                )}
              </div>
            )}

            {showCreateTaskForm && selectedDumpster && (
              <div className="mt-3" onClick={e => e.stopPropagation()}>
                <DumpsterTaskQuickCreateForm
                  clientId={location.client_id}
                  locationId={location.id}
                  onSubmit={() => {
                    setShowCreateTaskForm(false);
                    setSelectedDumpster(null);
                  }}
                  onCancel={() => {
                    setShowCreateTaskForm(false);
                    setSelectedDumpster(null);
                  }}
                />
              </div>
            )}

            {showEditLocationForm && selectedLocation && (
              <div className="mt-3 space-y-3" onClick={e => e.stopPropagation()}>
                <h3 className="font-medium text-gray-700">Modifier le point GPS</h3>
                
                <form onSubmit={handleEditLocationSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Nom du point
                    </label>
                    <input
                      type="text"
                      value={editLocationFormData.name}
                      onChange={(e) => setEditLocationFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Latitude
                      </label>
                      <input
                        type="text"
                        value={editLocationFormData.position_lat}
                        onChange={(e) => setEditLocationFormData(prev => ({ ...prev, position_lat: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Longitude
                      </label>
                      <input
                        type="text"
                        value={editLocationFormData.position_lng}
                        onChange={(e) => setEditLocationFormData(prev => ({ ...prev, position_lng: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {editLocationError && (
                    <div className="text-sm text-red-600">{editLocationError}</div>
                  )}

                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditLocationForm(false);
                        setEditLocationError(null);
                        setSelectedLocation(null);
                        setEditLocationFormData({
                          name: '',
                          position_lat: '',
                          position_lng: ''
                        });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    
                    <button
                      type="submit"
                      className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                    >
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            )}

            {showSaveGpsForm && (
              <div className="mt-3 space-y-3" onClick={e => e.stopPropagation()}>
                <input
                  type="text"
                  value={saveGpsFormData.name}
                  onChange={(e) => setSaveGpsFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom du point GPS"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                
                <div className="searchable-select">
                  <SearchableSelect
                    options={clientOptions}
                    value={saveGpsFormData.clientId}
                    onChange={(value) => setSaveGpsFormData(prev => ({ ...prev, clientId: value }))}
                    placeholder="Sélectionner un client"
                    searchPlaceholder="Rechercher un client..."
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={saveGpsFormData.isPrimary}
                    onChange={(e) => setSaveGpsFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Point principal</span>
                </label>

                {saveGpsError && (
                  <div className="text-sm text-red-600">{saveGpsError}</div>
                )}

                {saveGpsSuccess && (
                  <div className="text-sm text-green-600">Point GPS enregistré avec succès</div>
                )}

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSaveGpsForm(false);
                      setSaveGpsError(null);
                      setSaveGpsFormData({
                        name: '',
                        clientId: '',
                        isPrimary: false
                      });
                      setSelectedDumpster(null);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleSaveGpsSubmit}
                    className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </Popup>
        </Marker>
      ))}

      {depotMarkers.map(({ id, position, name }) => (
        <Marker
          key={`depot-${id}`}
          position={position}
          icon={depotIcon}
          eventHandlers={{
            click: () => onMarkerClick?.('depot', id)
          }}
        >
          <Popup>
            <div className="font-medium">{name}</div>
            <div className="text-sm text-gray-600">Dépôt</div>
            
            {(() => {
              const dumpstersInDepot = allDumpsters.filter(dumpster => dumpster.depotId === id);
              
              if (dumpstersInDepot.length > 0) {
                return (
                  <div className="mt-3 border-t pt-3">
                    <div className="font-medium text-sm mb-2">
                      Bennes dans ce dépôt ({dumpstersInDepot.length})
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {dumpstersInDepot.map(dumpster => (
                        <div key={dumpster.id} className="text-sm bg-gray-50 p-2 rounded">
                          <div className="font-medium">{dumpster.name}</div>
                          <div className="text-xs text-gray-600">Numéro: {dumpster.number}</div>
                          {dumpster.comment && (
                            <div className="text-xs text-gray-500 mt-1">{dumpster.comment}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="mt-3 border-t pt-3">
                    <div className="text-sm text-gray-500">Aucune benne dans ce dépôt</div>
                  </div>
                );
              }
            })()}
          </Popup>
        </Marker>
      ))}

      {dumpsterMarkers.map(({ positionKey, position, dumpsters: dumpstersAtPosition }) => (
        <Marker
          key={`dumpster-group-${positionKey}`}
          position={position}
          icon={dumpsterIcon}
          eventHandlers={{
            click: (e) => {
              e.originalEvent.stopPropagation();
              // For multiple dumpsters, we can use the first one's ID or create a composite ID
              const firstDumpster = dumpstersAtPosition[0];
              setSelectedDumpster({
                id: firstDumpster.id,
                position: position
              });
              onMarkerClick?.('dumpster', firstDumpster.id);
            }
          }}
        >
          <Popup>
            {dumpstersAtPosition.length === 1 ? (
              // Single dumpster - display as before
              <>
                <div className="font-medium">{dumpstersAtPosition[0].name}</div>
                <div className="text-sm text-gray-600">Benne {dumpstersAtPosition[0].number}</div>
                {dumpstersAtPosition[0].placedByClientName && (
                  <div className="text-sm text-blue-600">
                    Posée chez: {dumpstersAtPosition[0].placedByClientName}
                  </div>
                )}
                {dumpstersAtPosition[0].dropDate && dumpstersAtPosition[0].driver && (
                  <>
                    <div className="text-sm text-gray-600 mt-2">
                      Déposée le {format(new Date(dumpstersAtPosition[0].dropDate), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </div>
                    <div className="text-sm text-gray-600">
                      par {dumpstersAtPosition[0].driver.firstName} {dumpstersAtPosition[0].driver.lastName}
                      <span className="text-gray-400"> ({dumpstersAtPosition[0].driver.email})</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              // Multiple dumpsters - display list
              <>
                <div className="font-medium mb-2">
                  {dumpstersAtPosition.length} bennes à cette position
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {dumpstersAtPosition.map((dumpster, index) => (
                    <div key={dumpster.id} className="border-b border-gray-200 pb-2 last:border-b-0">
                      <div className="font-medium text-sm">{dumpster.name}</div>
                      <div className="text-xs text-gray-600">Numéro: {dumpster.number}</div>
                      {dumpster.placedByClientName && (
                        <div className="text-xs text-blue-600">
                          Posée chez: {dumpster.placedByClientName}
                        </div>
                      )}
                      {dumpster.dropDate && dumpster.driver && (
                        <>
                          <div className="text-xs text-gray-600">
                            Déposée le {format(new Date(dumpster.dropDate), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                          </div>
                          <div className="text-xs text-gray-600">
                            par {dumpster.driver.firstName} {dumpster.driver.lastName}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="mt-3 space-y-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Clicked "Enregistrer le point GPS" button for dumpster.');
                  setShowSaveGpsForm(true);
                }}
                className="w-full px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer le point GPS
              </button>
            </div>

            {showSaveGpsForm && (
              <div className="mt-3 space-y-3" onClick={e => e.stopPropagation()}>
                <input
                  type="text"
                  value={saveGpsFormData.name}
                  onChange={(e) => setSaveGpsFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom du point GPS"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                
                <div className="searchable-select">
                  <SearchableSelect
                    options={clientOptions}
                    value={saveGpsFormData.clientId}
                    onChange={(value) => setSaveGpsFormData(prev => ({ ...prev, clientId: value }))}
                    placeholder="Sélectionner un client"
                    searchPlaceholder="Rechercher un client..."
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={saveGpsFormData.isPrimary}
                    onChange={(e) => setSaveGpsFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Point principal</span>
                </label>

                {saveGpsError && (
                  <div className="text-sm text-red-600">{saveGpsError}</div>
                )}

                {saveGpsSuccess && (
                  <div className="text-sm text-green-600">Point GPS enregistré avec succès</div>
                )}

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSaveGpsForm(false);
                      setSaveGpsError(null);
                      setSaveGpsFormData({
                        name: '',
                        clientId: '',
                        isPrimary: false
                      });
                      setSelectedDumpster(null);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleSaveGpsSubmit}
                    className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MapComponent;